import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fmtDate, type TMatch, type Tournament, type TournamentSummary } from '../api';
import { useData } from '../App';
import { Empty, Icon, IconButton, MemberPicker } from '../ui';

/** mm:ss left until an instant, or null once it has passed. */
function useCountdown(endsAt: string | null): { text: string; over: boolean } | null {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  const over = ms <= 0;
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    text: `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`,
    over,
  };
}

/** Percentages are the whole point of a tiebreaker, so two decimals. */
const pct = (v: number) => (v * 100).toFixed(2);

type Score = { a: number; b: number; d: number };

/** Could a best-of-three produce this? Nobody past two, three games at most. */
const legal = ({ a, b, d }: Score) => a <= 2 && b <= 2 && a + b + d <= 3;

/**
 * Is the match over, rather than part-entered? The server applies the same
 * rule to decide when the next round may be paired (see `decided` in
 * routes/mot.ts); this copy is what greys a pairing out as you tap.
 */
const over = ({ a, b, d }: Score) => a >= 2 || b >= 2 || a + b + d >= 3 || (a === b && a + b + d >= 1);

const scoreOf = (m: TMatch): Score => ({
  a: m.a_games ?? 0,
  b: m.b_games ?? 0,
  d: m.game_draws ?? 0,
});

/** How long to sit on a tap before writing it, so a 2–1 is one request. */
const SAVE_AFTER_MS = 500;

/**
 * One pairing, scored the way Companion does it: tap the player who won the
 * game. Each tap moves that side on by one and wraps at two, so a mis-tap
 * costs a tap or two rather than a trip into a menu, and nothing has to be
 * matched against a list of possible scores.
 *
 * Taps land locally and are written once the tapping stops, which keeps a
 * 2–1 to a single request and means the number under your thumb never jumps
 * backwards while a save is in flight.
 */
function Pairing({
  m,
  nameOf,
  onReport,
}: {
  m: TMatch;
  nameOf: (id: number) => string;
  onReport: (mid: number, s: Score) => Promise<void>;
}) {
  const bye = m.b_player_id === null;
  const [draft, setDraft] = useState<Score>(() => scoreOf(m));
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Which tap we are waiting on. A save that lands after a newer tap must not
     declare the row settled, or the effect below would pull the stale server
     value back under the finger that just moved it. */
  const seq = useRef(0);

  // Follow the server whenever we are not in the middle of an edit — another
  // board member reporting the same match should show up here.
  useEffect(() => {
    if (dirty.current) return;
    setDraft(scoreOf(m));
  }, [m.a_games, m.b_games, m.game_draws]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  function push(next: Score) {
    setDraft(next);
    dirty.current = true;
    const mine = ++seq.current;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void onReport(m.id, next).finally(() => {
        if (seq.current === mine) dirty.current = false;
      });
    }, SAVE_AFTER_MS);
  }

  /** Next value for one side, skipping anything the format cannot produce. */
  function bump(side: 'a' | 'b') {
    for (let step = 1; step <= 3; step++) {
      const next = { ...draft, [side]: (draft[side] + step) % 3 };
      if (legal(next)) return push(next);
    }
  }

  const withDraw = { ...draft, d: draft.d > 0 ? 0 : 1 };
  const canDraw = legal(withDraw);
  const done = over(draft);

  const line = `${draft.a}–${draft.b}${draft.d > 0 ? `–${draft.d}` : ''}`;
  const resultText =
    !done && draft.a + draft.b + draft.d === 0
      ? 'Ekki skráð'
      : draft.a === draft.b
        ? `${line} · jafntefli`
        : `${line} · ${nameOf(draft.a > draft.b ? m.a_player_id : m.b_player_id!)}`;

  if (bye) {
    return (
      <div className="pairing bye">
        <span className="pr-side">
          <span className="pr-name">{nameOf(m.a_player_id)}</span>
          <span className="pr-box">2</span>
        </span>
        <span className="pr-vs">·</span>
        <span className="pr-side r">
          <span className="pr-box">0</span>
          <span className="pr-name">Bye</span>
        </span>
      </div>
    );
  }

  return (
    <div className={`pairing${done ? ' done' : ''}`}>
      <button type="button" className="pr-side tap" onClick={() => bump('a')}>
        <span className="pr-name">{nameOf(m.a_player_id)}</span>
        <span className="pr-box">{draft.a}</span>
      </button>
      <span className="pr-vs">vs</span>
      <button type="button" className="pr-side r tap" onClick={() => bump('b')}>
        <span className="pr-box">{draft.b}</span>
        <span className="pr-name">{nameOf(m.b_player_id!)}</span>
      </button>

      {/* What is actually stored, written out. Two tally boxes and a chip do
          not say whether the night's result was 1-1 or 1-1-1, and those are
          different game-win percentages. */}
      <div className="pr-foot">
        <span className="pr-result">{resultText}</span>

        {/* A drawn or unfinished game — the third game that ran out of time.
            It is what separates a 1-1 draw (3 game points each) from a
            1-1-1 one (4 each). */}
        {canDraw || draft.d > 0 ? (
          <button
            type="button"
            className={`pr-draw${draft.d > 0 ? ' on' : ''}`}
            onClick={() => canDraw && push(withDraw)}
            title="Leikur sem endaði jafn eða kláraðist ekki á tíma. Breytir ekki úrslitum leiksins, bara GW%."
          >
            {draft.d > 0 ? `${draft.d} leikur í jafntefli` : '+ leikur í jafntefli'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function Mot() {
  const { members, events, refresh } = useData();
  const [list, setList] = useState<TournamentSummary[]>([]);
  const [t, setT] = useState<Tournament | null>(null);
  const [name, setName] = useState('Sealed-mót');
  const [eventId, setEventId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => setList(await api.tournaments()), []);
  useEffect(() => void load(), [load]);

  const open = useCallback(async (id: number) => setT(await api.tournament(id)), []);
  const clock = useCountdown(t?.deckbuild_ends_at ?? null);
  const roundClock = useCountdown(t?.round_ends_at ?? null);

  /** Reporting a score should not grey out the board on every tap. */
  const report = useCallback(
    async (mid: number, sc: { a: number; b: number; d: number }) => {
      if (!t) return;
      setT(await api.reportMatch(t.id, mid, sc.a, sc.b, sc.d));
    },
    [t],
  );

  async function act<T>(fn: () => Promise<T>) {
    setBusy(true);
    try {
      const next = (await fn()) as unknown as Tournament;
      if (next && typeof next === 'object' && 'players' in next) setT(next);
      await load();
    } finally {
      setBusy(false);
    }
  }

  /* ── list ── */
  if (!t) {
    return (
      <div className="view">
        <section className="card col-8">
          <div className="card-h">
            <h2>Mót</h2>
            <span className="lbl">{list.length} skráð</span>
          </div>
          {list.length === 0 ? (
            <Empty title="Ekkert mót enn" body="Stofnaðu mót, bættu við spilurum og forritið parar saman." />
          ) : (
            <div className="todos">
              {list.map((x) => (
                <div key={x.id} className="todo">
                  <button type="button" className="tx tx-btn" onClick={() => void open(x.id)}>
                    <span className="tx-main">
                      {x.name}
                      {x.finished ? <span className="chip ev-tag">Lokið</span> : null}
                    </span>
                    <span className="ev-meta">
                      {x.playerCount} spilarar · {x.rounds} umferðir
                      {x.event_name ? ` · ${x.event_name}` : ''}
                    </span>
                  </button>
                  <IconButton
                    tone="del"
                    label={`Eyða ${x.name}`}
                    onClick={() =>
                      window.confirm('Eyða mótinu?') && void act(() => api.delTournament(x.id)).then(() => setT(null))
                    }
                  >
                    <Icon.trash />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h"><h2>Nýtt mót</h2></div>
          <div className="form">
            <div className="fld">
              <label className="lbl" htmlFor="mt-name">Heiti</label>
              <input id="mt-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="fld">
              <label className="lbl" htmlFor="mt-ev">Viðburður</label>
              <select
                id="mt-ev"
                value={eventId}
                onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Ótengt viðburði</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} · {fmtDate(ev.event_date)}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn"
              disabled={busy || !name.trim()}
              onClick={() =>
                void api
                  .addTournament({
                    name: name.trim(),
                    rounds: 3,
                    event_id: eventId === '' ? null : eventId,
                  })
                  .then((x) => {
                    setT(x);
                    void load();
                  })
              }
            >
              Stofna 3 umferða mót
            </button>
            <p className="hint" style={{ lineHeight: 1.5 }}>
              Svissneskt kerfi, best af þremur, eftir reglum Wizards (MTR). Pörun innan
              stigaflokka, engar endurtekningar, og tiebreakers OMW%, GW% og OGW% eins og í
              Companion. Sé mótið tengt viðburði skrást spilararnir sjálfkrafa á mætingu hans.
            </p>
          </div>
        </section>
      </div>
    );
  }

  /* ── one tournament ── */
  const inPlay = t.players.filter((p) => !p.dropped);
  const rounds = [...new Set(t.matches.map((m) => m.round_no))].sort((a, b) => b - a);
  const nameOf = (id: number) => t.players.find((p) => p.id === id)?.name ?? '—';
  const roster = t.players.map((p) => p.member_id);

  return (
    <div className="view">
      <section className="card col-12 mot-head">
        <button type="button" className="btn ghost sm" onClick={() => setT(null)}>← Öll mót</button>
        <h2>{t.name}</h2>
        <span className="lbl">
          {inPlay.length} spilarar · umferð {Math.max(t.roundsPlayed, 0)} af {t.rounds}
        </span>
        {/* Connecting the two is what stops the same ten names being typed in
            twice on a club night: the roster below becomes the event's
            attendance. */}
        <label className="lbl mot-ev">
          Viðburður
          <select
            value={t.event_id ?? ''}
            disabled={busy}
            onChange={(e) =>
              void act(() =>
                api.updateTournament(t.id, {
                  event_id: e.target.value ? Number(e.target.value) : null,
                }),
              ).then(refresh)
            }
          >
            <option value="">Ótengt</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name} · {fmtDate(ev.event_date)}
              </option>
            ))}
          </select>
        </label>
      </section>

      {/* deck construction clock — only before round one */}
      {t.roundsPlayed === 0 && (
      <section className={`card col-5 clock${clock?.over ? ' done' : ''}`}>
        <div className="card-h"><h2>Deck building</h2></div>
        {clock ? (
          <>
            <div className="clock-face">{clock.over ? 'TÍMI ÚTI' : clock.text}</div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn ghost sm" disabled={busy}
                onClick={() => void act(() => api.deckbuild(t.id, 50))}>Endurstilla 50</button>
              <button className="btn ghost sm" disabled={busy}
                onClick={() => void act(() => api.deckbuild(t.id, 0))}>Stöðva</button>
            </div>
          </>
        ) : (
          <>
            <p className="hint" style={{ marginBottom: 12 }}>
              Klukkan er geymd á þjóninum, svo allir símar sýna sama tíma.
            </p>
            <div className="row" style={{ gap: 8 }}>
              {[50, 40, 30].map((m) => (
                <button key={m} className="btn sm" disabled={busy}
                  onClick={() => void act(() => api.deckbuild(t.id, m))}>{m} mín</button>
              ))}
            </div>
          </>
        )}
      </section>
      )}

      {/* round clock — 50 minutes for a bo3, drawn at time */}
      {t.roundsPlayed > 0 && !t.finished && (
        <section className={`card col-7 clock${roundClock?.over ? ' done' : ''}`}>
          <div className="card-h">
            <h2>Umferð {t.roundsPlayed}</h2>
            <span className="lbl">
              {t.matches.filter((m) => m.round_no === t.roundsPlayed && m.a_games === null).length} ólokið
            </span>
          </div>

          {roundClock ? (
            <>
              <div className="clock-face">{roundClock.over ? 'TÍMI ÚTI' : roundClock.text}</div>
              <div className="row" style={{ gap: 8 }}>
                {roundClock.over && (
                  <button className="btn" disabled={busy}
                    onClick={() => void act(() => api.drawRemaining(t.id))}>
                    Skrá ólokin sem jafntefli
                  </button>
                )}
                <button className="btn ghost sm" disabled={busy}
                  onClick={() => void act(() => api.extendRound(t.id, 5))}>
                  +5 mín
                </button>
                <button className="btn ghost sm" disabled={busy}
                  onClick={() => void act(() => api.roundClock(t.id, t.round_minutes))}>
                  Endurstilla {t.round_minutes}
                </button>
                <button className="btn ghost sm" disabled={busy}
                  onClick={() => void act(() => api.roundClock(t.id, 0))}>Stöðva</button>
              </div>
            </>
          ) : (
            <>
              <p className="hint" style={{ marginBottom: 12 }}>
                Umferðarlengd er núna <b>{t.round_minutes} mín</b> — hún gildir líka fyrir næstu
                umferðir. Veldu aðra hér að neðan til að breyta henni.
              </p>
              <div className="row" style={{ gap: 8 }}>
                {[50, 40, 30].map((m) => (
                  <button key={m} className={`btn sm${m === t.round_minutes ? '' : ' ghost'}`}
                    disabled={busy}
                    onClick={() => void act(() => api.roundClock(t.id, m))}>Ræsa {m} mín</button>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* players — the same name-and-dropdown as attendance on an event.
          Before round one the roster can be edited freely; once play has
          started the only safe move is dropping someone, which keeps their
          results in the standings. */}
      <section className={`card col-${t.roundsPlayed === 0 ? '7' : '5'}`}>
        <div className="card-h">
          <h2>Spilarar</h2>
          <span className="lbl">{inPlay.length} í keppni</span>
        </div>

        {t.event_id ? (
          <p className="hint" style={{ marginBottom: 10 }}>
            Spilarar hér skrást sjálfkrafa á mætingu á <b>{t.event_name}</b>.
          </p>
        ) : null}

        {t.roundsPlayed === 0 ? (
          <>
            <MemberPicker
              members={members}
              selected={roster}
              onChange={(ids) => {
                // The roster lives on the server, so each change is one call.
                const added = ids.find((id) => !roster.includes(id));
                if (added !== undefined) return void act(() => api.addPlayer(t.id, added));
                const gone = roster.find((id) => !ids.includes(id));
                const row = t.players.find((p) => p.member_id === gone);
                if (row) void act(() => api.delPlayer(t.id, row.id));
              }}
              placeholder="Skrifaðu nafn til að bæta við…"
            />
            <p className="hint" style={{ marginTop: 10 }}>
              Þegar fyrsta umferð er pöruð læsist listinn; eftir það er spilari tekinn úr keppni
              en úrslitin hans standa.
            </p>
          </>
        ) : (
          <div className="pick" style={{ maxHeight: '13rem' }}>
            {t.players.map((p) => (
              <button key={p.id} type="button" className={`pk${p.dropped ? '' : ' on'}`}
                disabled={busy}
                title={p.dropped ? 'Kominn úr keppni — smelltu til að taka inn' : 'Smelltu til að taka úr keppni'}
                onClick={() => void act(() => api.dropPlayer(t.id, p.id, !p.dropped))}>
                {p.name}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* pairings */}
      <section className="card col-7">
        <div className="card-h">
          <h2>Pörun</h2>
          {t.canPairNext && (
            <button className="btn sm" disabled={busy} onClick={() => void act(() => api.pairRound(t.id))}>
              Para umferð {t.roundsPlayed + 1}
            </button>
          )}
        </div>

        {rounds.length === 0 ? (
          <Empty title="Engin umferð pöruð" body="Bættu við spilurum og ýttu á „Para umferð 1“." />
        ) : (<>
          <p className="hint" style={{ marginBottom: 12 }}>
            Ýttu á spilarann sem vann leikinn. Talan hækkar um einn og fer aftur í núll eftir tvo.
          </p>
          {rounds.map((r) => (
            <div key={r} className="round">
              <div className="round-h">
                <span className="lbl gold">Umferð {r}</span>
                {r === t.roundsPlayed && (
                  <IconButton tone="del" label="Afturkalla umferð"
                    onClick={() => window.confirm(`Afturkalla umferð ${r}?`) && void act(() => api.undoRound(t.id))}>
                    <Icon.trash />
                  </IconButton>
                )}
              </div>
              {t.matches
                .filter((m) => m.round_no === r)
                .map((m) => (
                  <Pairing key={m.id} m={m} nameOf={nameOf} onReport={report} />
                ))}
            </div>
          ))}
        </>)}
      </section>

      {/* standings — the same four tiebreakers, in the same order, as the
          Companion app: match points, then OMW%, then GW%, then OGW% */}
      <section className="card col-5">
        <div className="card-h">
          <h2>Staða</h2>
          <span className="lbl">MTR tiebreakers</span>
        </div>
        {t.standings.length === 0 ? (
          <Empty title="Engin úrslit enn" body="Staðan birtist þegar fyrsta umferð er skráð." />
        ) : (
          <div className="stand">
            <div className="st-row st-head">
              <span className="st-n">#</span>
              <span className="st-name">Spilari</span>
              <span className="st-pts">Stig</span>
              <span className="st-tb">OMW%</span>
              <span className="st-tb">GW%</span>
              <span className="st-tb">OGW%</span>
            </div>
            {t.standings.map((s, i) => (
              <div
                key={s.playerId}
                className={`st-row${i === 0 && t.roundsPlayed > 0 ? ' lead' : ''}${s.dropped ? ' out' : ''}`}
              >
                <span className="st-n">{i + 1}</span>
                <span className="st-name">
                  {s.name}
                  <span className="st-rec">
                    {s.wins}–{s.losses}–{s.draws}
                    {s.byes ? ` · ${s.byes > 1 ? `${s.byes} bye` : 'bye'}` : ''}
                    {s.dropped ? ' · úr keppni' : ''}
                  </span>
                </span>
                <span className="st-pts">{s.points}</span>
                <span className="st-tb">{pct(s.omw)}</span>
                <span className="st-tb">{pct(s.gw)}</span>
                <span className="st-tb">{pct(s.ogw)}</span>
              </div>
            ))}
          </div>
        )}
        <p className="hint" style={{ marginTop: 12, lineHeight: 1.5 }}>
          Stig: sigur 3, jafntefli 1. Raðað eftir stigum, svo OMW%, GW% og OGW% — sama röð og
          Companion notar. Lágmark 0,33 á öllum hlutföllum, svo 0–3 spilari telst 33%, og bye er
          2–0 sigur sem er sleppt úr OMW%/OGW% þess spilara.
        </p>
      </section>
    </div>
  );
}
